import React from "react";
import { Link } from "react-router-dom";
import {
  FaChartBar,
  FaDatabase,
  FaLandmark,
  FaMapMarkerAlt,
  FaUniversity,
} from "react-icons/fa";

const footerSections = [
  {
    title: "Religion Census & Analysis",
    links: [
      { to: "/", label: "Home" },
      { to: "/religion", label: "India Religion Data" },
      { to: "/india-census-2011", label: "Census 2011 Religion Hub" },
      { to: "/rankings/religion/hindu", label: "Hindu Population Rankings" },
      { to: "/rankings/religion/muslim", label: "Muslim Population Rankings" },
      { to: "/analysis/diversity-index", label: "Diversity Index" },
      { to: "/analysis/religion-gender-ratio", label: "Gender Ratio Analysis" },
      { to: "/analysis/religion-urban-rural", label: "Urban vs Rural Analysis" },
    ],
  },
  {
    title: "State Religion Data",
    links: [
      { to: "/religion/uttar-pradesh", label: "Uttar Pradesh Religion" },
      { to: "/religion/maharashtra", label: "Maharashtra Religion" },
      { to: "/religion/bihar", label: "Bihar Religion" },
      { to: "/religion/west-bengal", label: "West Bengal Religion" },
      { to: "/religion/telangana", label: "Telangana Religion" },
      { to: "/religion/tamil-nadu", label: "Tamil Nadu Religion" },
      { to: "/religion", label: "All States Religion Data" },
    ],
  },
  {
    title: "IFSC & Bank Discovery",
    links: [
      { to: "/ifsc", label: "IFSC Code Search" },
      { to: "/ifsc/banks", label: "IFSC Banks Directory" },
      { to: "/ifsc/bank/state-bank-of-india", label: "SBI IFSC Codes" },
      { to: "/ifsc/bank/hdfc-bank", label: "HDFC Bank IFSC Codes" },
      { to: "/ifsc/bank/icici-bank", label: "ICICI Bank IFSC Codes" },
      { to: "/ifsc/bank/axis-bank", label: "Axis Bank IFSC Codes" },
      { to: "/ifsc/bank/punjab-national-bank", label: "PNB IFSC Codes" },
    ],
  },
  {
    title: "Project & Legal",
    links: [
      { to: "/about-us", label: "About Us" },
      { to: "/contact", label: "Contact Us" },
      { to: "/data-correction", label: "Data Correction" },
      { to: "/privacy-policy", label: "Privacy Policy" },
      { to: "/terms-and-conditions", label: "Terms & Conditions" },
      { to: "/disclaimer", label: "Disclaimer" },
      { to: "/sitemap", label: "Website Sitemap" },
    ],
  },
];

const insightLinks = [
  { to: "/religion/delhi/central", label: "Delhi Religion Data" },
  { to: "/religion/telangana/hyderabad", label: "Hyderabad Religion Data" },
  { to: "/religion/maharashtra/pune", label: "Pune Religion Data" },
  { to: "/religion/karnataka/bangalore-urban", label: "Bangalore Religion Data" },
];

const Footer = () => {
  return (
    <footer
      className="border-top mt-5"
      style={{
        background: "linear-gradient(180deg, #f8fbff 0%, #eef4fb 100%)",
      }}
    >
      <div className="container py-5">
        <div className="row g-4 mb-4">
          <div className="col-xl-4 col-lg-5">
            <div className="d-flex align-items-center mb-3">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center me-3"
                style={{
                  width: "48px",
                  height: "48px",
                  background: "linear-gradient(135deg, #1d4ed8 0%, #0f766e 100%)",
                }}
              >
                <FaMapMarkerAlt className="text-white" />
              </div>
              <div>
                <div className="fw-bold text-primary fs-3">BharatVillages</div>
                <div className="text-muted small">Village, religion, IFSC and heritage discovery for India</div>
              </div>
            </div>

            <p className="text-muted small mb-4" style={{ lineHeight: "1.7" }}>
              BharatVillages brings together village census pages, religion insights, IFSC discovery, politics datasets,
              and landmark content into one searchable public archive designed for long-tail Indian queries.
            </p>

            <div className="row g-3">
              <div className="col-sm-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="small text-muted mb-1">Village Coverage</div>
                    <div className="h4 fw-bold mb-1">5.7L+</div>
                    <div className="small text-muted">Village detail pages in current dataset</div>
                  </div>
                </div>
              </div>
              <div className="col-sm-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="small text-muted mb-1">IFSC Network</div>
                    <div className="h4 fw-bold mb-1">7.7L+</div>
                    <div className="small text-muted">IFSC search and branch-location URLs</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-8 col-lg-7">
            <div className="row g-4">
              {footerSections.map((section) => (
                <div className="col-lg col-md-4 col-6" key={section.title}>
                  <h6 className="fw-bold mb-3 text-dark">{section.title}</h6>
                  <ul className="list-unstyled small mb-0">
                    {section.links.map((item) => (
                      <li className="mb-2" key={item.to}>
                        <Link to={item.to} className="text-decoration-none text-muted">
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="row g-4 mb-4">
          <div className="col-lg-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <h6 className="fw-bold mb-3 d-flex align-items-center">
                  <FaDatabase className="me-2 text-primary" />
                  Core Coverage
                </h6>
                <ul className="list-unstyled small text-muted mb-0">
                  <li className="mb-2">Census 2026 and 2027 help pages with form guidance</li>
                  <li className="mb-2">Census 2011 village records and subdistrict discovery</li>
                  <li className="mb-2">Religion demographics and district/state analysis</li>
                  <li className="mb-2">IFSC bank, branch and location search pages</li>
                  <li>Politics, heritage places and tourism-linked discovery</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <h6 className="fw-bold mb-3 d-flex align-items-center">
                  <FaUniversity className="me-2 text-primary" />
                  IFSC Shortcuts
                </h6>
                <div className="d-flex flex-wrap gap-2">
                  <Link to="/ifsc" className="badge rounded-pill text-bg-light border text-decoration-none">IFSC Search</Link>
                  <Link to="/ifsc/banks" className="badge rounded-pill text-bg-light border text-decoration-none">Banks Directory</Link>
                  <Link to="/ifsc/bank/state-bank-of-india" className="badge rounded-pill text-bg-light border text-decoration-none">SBI</Link>
                  <Link to="/ifsc/bank/bank-of-india" className="badge rounded-pill text-bg-light border text-decoration-none">Bank of India</Link>
                  <Link to="/ifsc/bank/hdfc-bank" className="badge rounded-pill text-bg-light border text-decoration-none">HDFC Bank</Link>
                  <Link to="/ifsc/bank/punjab-national-bank" className="badge rounded-pill text-bg-light border text-decoration-none">PNB</Link>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <h6 className="fw-bold mb-3 d-flex align-items-center">
                  <FaChartBar className="me-2 text-primary" />
                  Popular Insights
                </h6>
                <ul className="list-unstyled small mb-0">
                  {insightLinks.map((item) => (
                    <li className="mb-2" key={item.to}>
                      <Link to={item.to} className="text-decoration-none text-muted">
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 pt-4 border-top small text-muted">
          <div>
            © {new Date().getFullYear()} <span className="fw-bold">BharatVillages.com</span>. Rural data, IFSC discovery and long-tail India pages.
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="badge bg-primary-subtle text-primary-emphasis border">
              <FaLandmark className="me-1" />
              Census 2011 Archive
            </span>
            <span className="badge bg-primary-subtle text-primary-emphasis border">
              Search-First Discovery
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
